import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { divIcon } from 'leaflet';
import { useAlarmSound } from '../hooks/useAlarmSound';
import { fmtDateTime } from '../utils/date';
import type { PendingAlarm } from '../types';

interface Props {
  alarm: PendingAlarm;
  onDismiss: (alarmId: string, notes?: string) => void;
}

const alarmMarker = divIcon({
  className: '',
  html: `<div style="width:24px;height:24px;background:#dc2626;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 4px rgba(220,38,38,.4);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

export default function AlarmModal({ alarm, onDismiss }: Props) {
  const [notes, setNotes] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const { prime } = useAlarmSound(true);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  // Prime AudioContext (user interaction is already happening since modal rendered on ws message
  // which requires an open browser tab; prime on mount for safety)
  useEffect(() => { prime(); }, [prime]);

  // Elapsed time counter
  useEffect(() => {
    const start = alarm.received_at;
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [alarm.received_at]);

  function handleAck() {
    onDismiss(alarm.alarm_id, notes.trim() || undefined);
  }

  const raisedAt = fmtDateTime(alarm.raised_at);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70"
      // Intentionally NOT dismissable by clicking backdrop
    >
      {/* Pulsing red ring animation */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 animate-ping bg-alarm-600/10 rounded-none" style={{ animationDuration: '1s' }} />
      </div>

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-alarm-600 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl animate-bounce">🚨</span>
            <div>
              <h2 className="text-xl font-bold text-white">ALARM RAISED</h2>
              <p className="text-alarm-100 text-sm">{elapsed}s ago · raised at {raisedAt}</p>
            </div>
          </div>
        </div>

        {/* Guard info */}
        <div className="px-6 py-4 border-b">
          <p className="text-lg font-semibold text-gray-900">{alarm.guard_name}</p>
          <p className="text-sm text-gray-500">
            {alarm.lat.toFixed(5)}, {alarm.lng.toFixed(5)}
          </p>
        </div>

        {/* Mini map */}
        <div className="h-52">
          <MapContainer
            center={[alarm.lat, alarm.lng]}
            zoom={16}
            className="h-full w-full"
            style={{ zIndex: 0 }}
            zoomControl={false}
            attributionControl={false}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[alarm.lat, alarm.lng]} icon={alarmMarker} />
          </MapContainer>
        </div>

        {/* Notes + Acknowledge */}
        <div className="px-6 py-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Acknowledgement note <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            ref={notesRef}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="e.g. Dispatched response team to north gate"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-alarm-500 resize-none"
          />
          <button
            onClick={handleAck}
            className="mt-3 w-full py-3 rounded-xl bg-alarm-600 hover:bg-alarm-700 text-white font-bold text-lg transition-colors"
          >
            Acknowledged
          </button>
        </div>
      </div>
    </div>
  );
}
